import React from 'react'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '@/lib/utils'

const buttonVariants = cva(
  'inline-flex items-center whitespace-nowrap text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gray-950 disabled:pointer-events-none disabled:opacity-50',
  {
    variants: {
      variant: {
        default:
          'text-gray-900 border dark:border-gray-700 border-gray-100 shadow-card hover:bg-gray-50',
        subtle:
          'border border-dashed shadow-card bg-gray-50/50 text-gray-500 font-normal',
      },
      size: { default: 'h-9 px-4 py-2', lg: 'h-16 rounded-lg px-4' },
    },
    defaultVariants: {
      variant: 'default',
      size: 'default',
    },
  },
)

export interface ShortcutButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  icon?: Function
  asChild?: boolean
}

export const ShortcutButton = React.forwardRef<
  HTMLButtonElement,
  ShortcutButtonProps
>(({ className, variant, icon, asChild, size, children, ...props }, ref) => {
  const Comp = asChild ? Slot : 'button'
  return (
    <Comp
      ref={ref}
      className={cn(buttonVariants({ variant, size, className }))}
      {...props}
    >
      <div className="flex w-full items-center">
        {icon && (
          <span className="mr-2">
            {icon({ className: 'size-6', strokeWidth: 1.2 })}
          </span>
        )}
        {children}
      </div>
    </Comp>
  )
})

ShortcutButton.displayName = 'Button'
